import { getPasswordIssues, type PasswordIssue } from '@caterinx/shared-types';
import { Alert, AlertDescription, AlertTitle } from '@caterinx/ui/components/ui/alert';
import { Button } from '@caterinx/ui/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@caterinx/ui/components/ui/card';
import { Input } from '@caterinx/ui/components/ui/input';
import { Label } from '@caterinx/ui/components/ui/label';
import { PasswordInput } from '@caterinx/ui/components/ui/password-input';
import { Skeleton } from '@caterinx/ui/components/ui/skeleton';
import { Wordmark } from '@caterinx/ui/components/ui/wordmark';
import { useToast } from '@caterinx/ui/hooks/use-toast';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { api } from '../../lib/api.js';
import { recaptcha } from '../../lib/recaptcha.js';

const ISSUE_TEXT: Record<PasswordIssue, string> = {
  too_short: 'Mindestens 8 Zeichen',
  too_long: 'Höchstens 256 Zeichen',
  whitespace_edges: 'Keine Leerzeichen am Anfang oder Ende',
  too_common: 'Kein offensichtlich unsicheres Passwort',
};

interface InvitePreview {
  companyName: string;
  role: 'owner' | 'admin' | 'member';
  /** Die eingeladene Adresse. Wird schreibgeschützt angezeigt, nicht abgefragt. */
  email: string;
  expiresAt: string;
}

/**
 * `/einladung?token=…` — der Weg, über den die ersten zehn Partner reinkommen.
 *
 * Der Betrieb wurde vom Admin angelegt (Handanlage), der Gastronom bekommt einen Link und
 * vergibt hier sein Passwort — **ohne vorherige Registrierung**. Derselbe Weg gilt für
 * eingeladene Kollegen; unterschieden wird nur über die Rolle in der Einladung.
 *
 * **Die E-Mail-Adresse wird angezeigt, nicht abgefragt.** Sie steht fest: Die Einladung ging an
 * genau diese Adresse, und wer den Link geöffnet hat, hat die Mail dort bekommen. Ein Feld zum
 * Eintippen wäre nur eine Gelegenheit für einen Tippfehler — und der endete in einer Absage
 * („Die Einladung gilt für eine andere E-Mail-Adresse"), die der Nutzer nicht selbst
 * auflösen kann. `preSignUp` prüft die Adresse serverseitig weiterhin; das ist der eigentliche
 * Schutz und greift auch, wenn jemand direkt gegen die Cognito-API registriert.
 */
export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { toast } = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  /**
   * **Auch der Einladungsweg braucht ein reCAPTCHA-Token.** Bis zum 29.08.2026 fehlte es hier,
   * und in prod war damit der einzige offene Weg in die Plattform blockiert: `register.ts`
   * verlangt das Token unabhängig davon, ob eine Einladung vorliegt, `supplierSelfSignup`
   * steht in prod auf `false`, und jeder eingeladene Betrieb lief in „reCAPTCHA-Token fehlt."
   *
   * Auf staging war das nicht sichtbar — dort prüft das Backend mit `NODE_ENV=development`
   * gar nicht, und die Registrierung ging ohne Token durch.
   *
   * **Warum nicht stattdessen der Server nachgibt:** Ein „Einladungstoken vorhanden, also kein
   * Captcha" ließe sich mit einer beliebigen Zeichenkette auslösen — die Gültigkeit prüft erst
   * `preSignUp` danach. Das wäre ein captcha-freier Weg zur Cognito-Registrierung. Der Aufwand
   * liegt hier richtiger: dasselbe Token, dieselbe Bedingung wie bei der Selbstregistrierung.
   *
   * Die Einwilligungsabfrage, die hier bis zum 21.09.2026 stand, ist mit D27 entfallen —
   * reCAPTCHA läuft jetzt auf berechtigtem Interesse.
   */

  /** Ohne Site-Key (lokale Entwicklung) läuft keine Bot-Abwehr und es gibt nichts zu holen. */
  const botProtectionRequired = recaptcha.siteKey !== '';

  const passwordIssues = useMemo(() => (password ? getPasswordIssues(password) : []), [password]);

  const {
    data: invite,
    isPending,
    isError,
  } = useQuery({
    queryKey: ['invite', token],
    queryFn: async () => (await api.get<InvitePreview>(`/invites/${encodeURIComponent(token)}`)).data,
    enabled: token !== '',
    retry: false,
  });

  const handleSubmit = async (formEvent: FormEvent, inviteEmail: string): Promise<void> => {
    formEvent.preventDefault();

    setIsSubmitting(true);

    try {
      const recaptchaToken = await recaptcha.token('REGISTER');

      if (botProtectionRequired && recaptchaToken === null) {
        toast({
          variant: 'destructive',
          title: 'Bot-Schutz konnte nicht geladen werden',
          description:
            'Das Sicherheitsmodul von Google ist nicht angelaufen. Meist liegt es an einem ' +
            'Werbe- oder Skriptblocker — bitte für diese Seite ausnehmen und erneut versuchen. ' +
            'Bleibt es dabei, schreiben Sie uns an angebote@caterinx.com.',
        });
        return;
      }

      await api.post('/auth/register', {
        email: inviteEmail,
        password,
        contactName: name.trim(),
        inviteToken: token,
        recaptchaToken,
        siteKey: recaptcha.siteKey || undefined,
      });

      toast({ title: 'Konto angelegt', description: 'Wir haben Ihnen einen Bestätigungscode geschickt.' });
      navigate(`/verify-email?email=${encodeURIComponent(inviteEmail)}`);
    } catch (error) {
      const data = isAxiosError(error) ? (error.response?.data as { code?: string; message?: string }) : undefined;

      toast({
        variant: 'destructive',
        title: 'Zugang konnte nicht eingerichtet werden',
        description:
          data?.code === 'EMAIL_EXISTS'
            ? 'Zu dieser Adresse gibt es bereits ein Konto. Melden Sie sich einfach an.'
            : // Die Meldungen aus dem `preSignUp`-Trigger („Die Einladung gilt für eine andere
              // E-Mail-Adresse.") sind für Nutzer geschrieben und werden hier durchgereicht.
              (data?.message ?? 'Bitte versuchen Sie es in einem Moment erneut.'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <CenteredCard title="Link unvollständig">
        <Alert variant="destructive">
          <AlertTitle>Kein Einladungstoken</AlertTitle>
          <AlertDescription>
            Bitte öffnen Sie den Link aus der E-Mail vollständig — inklusive des Teils hinter dem Fragezeichen.
          </AlertDescription>
        </Alert>
      </CenteredCard>
    );
  }

  if (isPending) {
    return (
      <CenteredCard title="Einladung wird geprüft">
        <Skeleton className="h-24 w-full" />
      </CenteredCard>
    );
  }

  if (isError || !invite) {
    return (
      <CenteredCard title="Einladung ungültig">
        <Alert variant="destructive">
          <AlertTitle>Dieser Link funktioniert nicht mehr</AlertTitle>
          <AlertDescription>
            Einladungen sind 14 Tage gültig. Bitte fordern Sie unter angebote@caterinx.com eine neue an.
          </AlertDescription>
        </Alert>
        <p className="text-muted-foreground mt-4 text-sm">
          Sie haben bereits ein Konto?{' '}
          <Link to="/login" className="underline">
            Anmelden
          </Link>
        </p>
      </CenteredCard>
    );
  }

  return (
    <CenteredCard
      title={`Zugang für ${invite.companyName}`}
      description={
        invite.role === 'owner'
          ? 'Vergeben Sie ein Passwort. Danach vervollständigen Sie Ihr Betriebsprofil.'
          : `Sie wurden als ${invite.role === 'admin' ? 'Administrator' : 'Mitarbeiter'} eingeladen.`
      }
    >
      <form onSubmit={(formEvent) => void handleSubmit(formEvent, invite.email)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">E-Mail</Label>
          {/*
            Schreibgeschützt, aber sichtbar: Die Adresse ist Teil der Einladung und nicht
            wählbar. `readOnly` statt `disabled`, damit Passwortmanager sie als Benutzernamen
            erkennen — ein `disabled`-Feld wird von den meisten ignoriert.
          */}
          <Input id="email" type="email" readOnly autoComplete="username" value={invite.email} className="bg-muted" />
          <p className="text-muted-foreground text-xs">
            An diese Adresse ging die Einladung. Sie wird zugleich Ihre Anmeldung.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Ihr Name</Label>
          <Input
            id="name"
            autoComplete="name"
            value={name}
            onChange={(changeEvent) => setName(changeEvent.target.value)}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Passwort</Label>
          <PasswordInput
            id="password"
            required
            autoComplete="new-password"
            value={password}
            onChange={(changeEvent) => setPassword(changeEvent.target.value)}
          />
          {passwordIssues.length > 0 && (
            <ul className="text-destructive space-y-1 text-xs">
              {passwordIssues.map((issue) => (
                // eslint-disable-next-line security/detect-object-injection -- `issue` ist ein PasswordIssue aus getPasswordIssues()
                <li key={issue}>{ISSUE_TEXT[issue]}</li>
              ))}
            </ul>
          )}
        </div>

        <Button
          type="submit"
          className="min-h-11 w-full"
          disabled={password === '' || passwordIssues.length > 0 || isSubmitting}
        >
          {isSubmitting ? 'Wird eingerichtet...' : 'Zugang einrichten'}
        </Button>
      </form>
    </CenteredCard>
  );
}

function CenteredCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  /*
   * Der zusätzliche Fußraum für das Einwilligungsbanner ist mit D27 entfallen: Im Portal läuft
   * kein einwilligungspflichtiger Dienst mehr, also steht dort auch kein Banner, das den Knopf
   * verdecken könnte.
   */
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-8 p-4">
      <Wordmark className="w-44" />
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </div>
  );
}
